<template>
  <div class="care-summary mb-0">
<!--    <div class="fw-bolder text-uppercase text-muted mt-4 text-start mt-4">{{ userRole !== 'SYSTEM_USER' ? "Price overview" : "Financial details" }}</div>-->
<!--    <hr class="m-0">-->
    <div v-if="boostComparison" class="row">
      <div v-for="field in pricingFields" :key="field.key" class="col-md-4 pt-2">
        <div class="form-group has-label mb-0">
          <div class="form-label fw-bold">{{ $t(field.label) }}</div>
          <div class="care-price-comparison">
            <del v-if="boostComparison.standard[field.key] !== boostComparison.boosted[field.key]" class="care-price-comparison__previous">
              <span class="visually-hidden">{{ $t("Standard price") }}: </span>{{ formatPrice(boostComparison.standard[field.key]) }}
            </del>
            <strong><span class="visually-hidden">{{ $t("Boost price") }}: </span>{{ formatPrice(boostComparison.boosted[field.key]) }}</strong>
          </div>
        </div>
      </div>
      <p class="small mt-3 mb-0" role="status">{{ $t(boostComparison.applied ? "Boost pricing applied. The organisation total is unchanged." : "Automatic Boost preview. These prices will apply when the scheduler activates Boost; the organisation total stays the same.") }}</p>
    </div>
    <div v-else class="row">
      <div v-if="userRole !== 'CLIENT'" class="col-md-4 pt-2">
        <div v-if="employeeOriginalEarnings !== null" class="form-group has-label mb-0">
          <div class="form-label fw-bold">{{ $t("Earnings") }}</div>
          <div class="care-price-comparison">
            <del class="care-price-comparison__previous" :title="$t('Estimated earnings before Boost')">
              <span class="visually-hidden">{{ $t("Estimated earnings before Boost") }}: </span>≈ {{ formatPrice(employeeOriginalEarnings) }}
            </del>
            <strong><span class="visually-hidden">{{ $t("Boosted earnings") }}: </span>{{ formatPrice(cost.employeeEarnings) }}</strong>
          </div>
          <small class="employee-boost-label">{{ $t("Boosted earnings") }}</small>
        </div>
        <display-label
            v-else
            class="mb-0"
            :label="userRole === 'EMPLOYEE' ? 'Earnings' : 'Employee earnings'"
            :value="getValueOrDefault(cost ?? {}, 'employeeEarnings', '' )/100+'€' ?? ''"
        ></display-label>
        <!--            <span class="text-sm fst-italic">Earnings is the amount that you will earn by this shift </span>-->
      </div>
      <div v-if="userRole !== 'CLIENT'" class="col-md-4 pt-2">
        <div v-if="employeeOriginalEarnings !== null" class="form-group has-label mb-0">
          <div class="form-label fw-bold">{{ $t("Commission") }}</div>
          <div class="care-price-comparison">
            <del class="care-price-comparison__previous" :title="$t('Estimated commission before Boost')">
              <span class="visually-hidden">{{ $t("Estimated commission before Boost") }}: </span>≈ {{ formatPrice(cost.totalCost - employeeOriginalEarnings) }}
            </del>
            <strong><span class="visually-hidden">{{ $t("Reduced commission") }}: </span>{{ formatPrice(cost.companyCommission) }}</strong>
          </div>
          <small class="employee-boost-label">{{ $t("Reduced commission") }}</small>
        </div>
        <display-label
            v-else
            class="mb-0"
            :label="userRole !== 'SYSTEM_USER' ? 'Commission' : 'Company commission'"
            :value="getValueOrDefault(cost ?? {}, 'companyCommission', '' )/100+'€' ?? ''"
        ></display-label>
        <!--            <span class="text-sm fst-italic">Commission will be paid by you in an Invoice by AllCare</span>-->
      </div>
      <div :class="{ 'col-md-12' : userRole === 'CLIENT'}" class="col-md-4 pt-2">
        <display-label
            class="mb-0"
            :label="userRole === 'EMPLOYEE' ? 'Shift Price' : 'Total cost'"
            :value="getValueOrDefault(cost ?? {}, 'totalCost', '' )/100+'€' ?? ''"
        ></display-label>
        <!--            <span v-if="userRole === 'EMPLOYEE'" class="text-sm fst-italic">Shift price will be paid by the Client directly to you</span>-->
      </div>
    </div>
    <div v-if="isLastMinute && userRole !== 'EMPLOYEE'" class="mt-3 alert alert-warning py-2 mb-0" role="status">
      <strong>{{ userRole === "CLIENT" ? $t("Same total · Higher employee earnings") : $t("Last-minute pricing · Reduced commission") }}</strong>
      <span class="d-block">{{ userRole === "CLIENT" ? $t("Higher employee earnings at no extra cost to your organisation.") : $t("The organisation total is unchanged and the commission is reduced.") }}</span>
    </div>
  </div>
</template>

<script>
import {getBoostPricingComparison} from "@/helpers/boostPricing"
import {getStatusClass} from "@/helpers/common"
import DisplayLabel from "@/components/DisplayLabel"

export default {
  name: "RequestPricingCard",
  components: {DisplayLabel},
  props: {
    cost: {
      type: Object,
      description: "request cost data",
    },
    isLastMinute: { type: Boolean, default: false },
    request: {type: Object, default: null},
    standardCost: {type: Object, default: null},
  },
  data() {
    return {
      now: Date.now(),
      clockTimer: null,
      pricingFields: [
        {key: "employeeEarnings", label: "Employee earnings"},
        {key: "companyCommission", label: "Company commission"},
        {key: "totalCost", label: "Total cost"}
      ]
    }
  },
  computed: {
    boostComparison() {
      if (this.userRole !== "SYSTEM_USER") return null
      return getBoostPricingComparison(this.request ? {
        ...this.request,
        cost: this.cost,
        isLastMinute: this.isLastMinute || this.request.isLastMinute
      } : null, this.standardCost, this.now)
    },
    userRole(){
      return this.$store.state.user.claims.role ?? null
    },
    employeeOriginalEarnings() {
      if (this.userRole !== "EMPLOYEE" || !this.isLastMinute) return null
      const {totalCost, companyCommission, employeeEarnings} = this.cost ?? {}
      if (![totalCost, companyCommission, employeeEarnings].every(value => Number.isSafeInteger(value) && value >= 0) || companyCommission <= 0 || employeeEarnings + companyCommission !== totalCost) return null
      // The backend stores only the rounded half-commission, so the original
      // earnings can differ by one cent. Mark the reconstructed value as estimated.
      const original = totalCost - companyCommission * 2
      return original >= 0 && original < employeeEarnings ? original : null
    },
  },
  mounted() {
    if (this.userRole === "SYSTEM_USER") this.clockTimer = setInterval(() => { this.now = Date.now() }, 1000)
  },
  beforeUnmount() {
    clearInterval(this.clockTimer)
  },
  methods: {
    formatPrice(cents) {
      return new Intl.NumberFormat(this.$i18n.locale === "gr" ? "el-GR" : this.$i18n.locale, {style: "currency", currency: this.cost?.currency || "EUR"}).format(cents / 100)
    },
    getStatusClass,
    getValueOrDefault(obj, prop, defaultValue){
      return Object.prototype.hasOwnProperty.call(obj, prop) ? (obj[prop] || defaultValue) : defaultValue
    }

  },
}
</script>

<style scoped>
.employee-boost-label { display: block; margin-top: 5px; color: var(--care-success-ink); font-size: 11px; font-weight: 600; }
</style>
